import { useFeatureLocks } from '@/hooks/useFeatureLocks';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CartButton } from '@/components/DesignSystem/AtomicComponents';
import NotifyProduct from '@/components/NotifyProduct';
import { deviceWidth } from '@/utils/deviceWidth';
import { isNotifyProductEnabled } from '@/utils/isNotifyProductEnabled';
import { MAX_MOBILE_WIDTH } from '@/utils/constants';
import getCustomCtaText from '@/utils/getCustomCtaText';
import { CartCtaDesktopContainer } from './CartCta.styles';
import LeftCartButton from './LeftCartButton';
import RightCartButton from './RightCartButton';
import { checkProductPersonalizationExist } from '../../Utils/common';
import { useCartHandlers } from '../../../../hooks/useCartHandler';
import { IS_CLIENT } from '../../../../utils/checkRenderEnv';
import { setUnavailableQuantityAlertState } from 'src/redux/actions';

function CartCtaDesktop(props) {
  const {
    handleCart,
    goToCart,
    itemIndexInCart,
    onBuyNowClick,
    returnItemData,
    disabledAdditionComponent,
    variantOutOfStock,
    product,
    isPDPPage,
    activeVariant,
    schedualerConstraints,
    scheduleInfo,
    setShowSchedulerError,
  } = props;
  const isMobile = deviceWidth <= MAX_MOBILE_WIDTH;
  const { storeInfo } = useSelector((state) => ({
    storeInfo: state.storeReducer.store,
  }));
  const dispatch = useDispatch();
  const { notifyMeFeatureStatus } = useFeatureLocks();
  const isNotifyMeFeatureVisible = notifyMeFeatureStatus?.isVisible;
  const {
    handleIncreamentClick,
    handleDecreamentClick,
    handleSetProductData,
    handleSetProductCustomizationCount,
    onPersonalizeProductClick,
  } = useCartHandlers();

  if (isMobile) return null;

  const isProductPersonalizationAvailable = checkProductPersonalizationExist(
    storeInfo?.services?.store_type_flag,
    product?.product_personalization
  );

  /**
   * This function is responsible to perform 3 tasks:
   * 1. Set the active product data in personalisedProduct state of catalogReducer
   * 2. Set the product customization config ( image count, text count etc ) in personalisedProduct state of catalogReducer
   * 3. Open the product personalization popup
   */
  function openProductPersonalizationPopUp() {
    handleSetProductData(product);
    handleSetProductCustomizationCount(product);
    onPersonalizeProductClick();
  }

  function handleCartAction(type) {
    if (isProductPersonalizationAvailable) {
      if (activeVariant) {
        IS_CLIENT && localStorage.setItem('activeVariantId', activeVariant?.variant_id);
      }
      type === 'inc'
        ? handleIncreamentClick(type, product)
        : handleDecreamentClick(type, product);
    } else {
      handleCart(type);
    }
  }

  function renderAddToCart() {
    return (
      <Button
        label="Add to Cart"
        variant="outline"
        borderColor={storeInfo?.theme?.colors?.primary_color}
        backgroundColor={storeInfo?.theme?.colors?.primary_color}
        fontSize="18"
        px="12px"
        py="12px"
        ml="22px"
        width="256px"
        roundness={storeInfo?.theme?.roundness}
        onClick={() => {
          if (schedualerConstraints && !scheduleInfo) {
            setShowSchedulerError(true);
            return;
          }
          isProductPersonalizationAvailable
            ? openProductPersonalizationPopUp()
            : handleCart('add');
        }}
      />
    );
  }

  function renderBuyNow() {
    return (
      <Button
        borderColor="#EDEDED"
        borderWidth="0px"
        label={storeInfo?.theme?.cta_config?.pdp_cta || getCustomCtaText('buy_now_cta')}
        backgroundColor={storeInfo?.theme?.colors?.primary_color}
        fontSize="18"
        px="12px"
        py="12px"
        width="256px"
        roundness={storeInfo?.theme?.roundness}
        onClick={() => {
          if (schedualerConstraints && !scheduleInfo) {
            setShowSchedulerError(true);
            return;
          }
          isProductPersonalizationAvailable
            ? openProductPersonalizationPopUp()
            : onBuyNowClick(null, scheduleInfo);
        }}
      />
    );
  }

  function personalizeAndBuyCTA() {
    return (
      <div className="flex flex-row mt24px w-100">
        <Button
          borderColor="#EDEDED"
          borderWidth="0px"
          label={
            storeInfo?.theme?.cta_config?.pdp_personalized_cta || 'Personalise and buy'
          }
          backgroundColor={storeInfo?.theme?.colors?.primary_color}
          fontSize="18"
          px="12px"
          py="12px"
          width="256px"
          roundness={storeInfo?.theme?.roundness}
          onClick={() => {
            if (schedualerConstraints && !scheduleInfo) {
              setShowSchedulerError(true);
              return;
            }
            openProductPersonalizationPopUp();
          }}
        />
      </div>
    );
  }

  const handleOnChange = (type = 'add', count) =>
    isProductPersonalizationAvailable
      ? openProductPersonalizationPopUp()
      : handleCart(type, count);

  const handleItemChange = (type, count = null, reset = null) => {
    const selectedProductInfo = returnItemData().selectedItem;
    if (
      count &&
      selectedProductInfo?.managed_inventory &&
      selectedProductInfo?.available_quantity < count
    ) {
      dispatch(
        setUnavailableQuantityAlertState({
          show: true,
          availableQuantity: selectedProductInfo?.available_quantity,
          onConfirm: () => handleOnChange(type, selectedProductInfo?.available_quantity),
          onCancel: reset,
        })
      );
    } else {
      handleOnChange(type, count);
    }
  };

  function renderCartMinusQuantityPlus() {
    return (
      <CartButton
        allowCustomInput={!product?.product_personalization}
        leftButton={
          <LeftCartButton
            isPDPPage={isPDPPage}
            iconWidth={18}
            handleCart={handleCartAction}
          />
        }
        rightButton={
          <RightCartButton
            disabledAdditionComponent={disabledAdditionComponent}
            handleCart={handleCartAction}
            iconWidth={18}
            isPDPPage={isPDPPage}
          />
        }
        label={
          isProductPersonalizationAvailable
            ? storeInfo?.theme?.cta_config?.pdp_personalized_cta || 'Personalise and buy'
            : null
        }
        primaryColor={storeInfo?.theme?.colors?.primary_color}
        fontSize="18"
        roundness={storeInfo?.theme?.roundness}
        onChange={handleItemChange}
        quantity={returnItemData().quantity}
        centered={true}
        ml="22px"
        width="256px"
        height={'unset'}
        isPDPPage={isPDPPage}
        wrapperClasses="tw-h-full"
      />
    );
  }

  function renderGoToCart() {
    return (
      <CartCtaDesktopContainer onClick={goToCart} cartCtaButtonWidth="256px">
        Go to Cart
      </CartCtaDesktopContainer>
    );
  }

  function renderOutOfStock() {
    return (
      <Button
        label={'Out Of Stock'}
        backgroundColor={'#C6C6C6'}
        fontSize="18"
        px="12px"
        py="12px"
        mt="32px"
        width="100%"
        roundness={storeInfo?.theme?.roundness}
      />
    );
  }

  if (isNotifyMeFeatureVisible && isNotifyProductEnabled() && !product.available)
    return <NotifyProduct product={product} />;

  if (!product.available) return renderOutOfStock();

  if (variantOutOfStock) return renderOutOfStock();

  const renderCTA = () => {
    if (storeInfo?.services?.store_flag === 0) {
      return null;
    }

    if (isProductPersonalizationAvailable && itemIndexInCart === -1) {
      return personalizeAndBuyCTA();
    }

    return (
      <div className="flex flex-row w-100 tw-mt-[32px]">
        {itemIndexInCart === -1 ? renderBuyNow() : renderGoToCart()}
        {itemIndexInCart === -1 ? renderAddToCart() : renderCartMinusQuantityPlus()}
      </div>
    );
  };

  return <>{renderCTA()}</>;
}

export default CartCtaDesktop;
